import { useRef, useEffect, useCallback, type ReactNode, type CSSProperties } from 'react';

interface CardProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  bento?: boolean;
}

export function Card({ children, className = '', style, onClick, selected, disabled, bento = false }: CardProps) {
  const interactive = !!onClick;
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const el = cardRef.current;
    if (!el || disabled) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;

    // Spotlight position
    el.style.setProperty('--mouse-x', `${x}px`);
    el.style.setProperty('--mouse-y', `${y}px`);

    // 3D tilt — subtle for dark UI
    const rotateX = (y - h / 2) / 30;
    const rotateY = -(x - w / 2) / 30;
    el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = 'perspective(800px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
  }, []);

  useEffect(() => {
    if (!bento) return;
    const el = cardRef.current;
    if (!el) return;

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [bento, handleMouseMove, handleMouseLeave]);

  return (
    <div
      ref={cardRef}
      data-bento={bento || undefined}
      className={`
        relative overflow-hidden bg-white/[0.02] border rounded-xl p-8 backdrop-blur-md
        ${selected
          ? 'border-[rgba(60,160,220,0.3)] bg-white/[0.05] shadow-[0_0_30px_rgba(60,160,220,0.08)]'
          : 'border-white/[0.05]'}
        ${interactive && !disabled
          ? 'cursor-pointer hover:border-white/[0.12] hover:bg-white/[0.035] transition-all duration-300'
          : ''}
        ${bento && !disabled ? '[transition:border-color_0.3s,background-color_0.3s]' : ''}
        ${disabled ? 'opacity-40 cursor-not-allowed' : ''}
        ${className}
      `}
      style={{ ...(bento ? { willChange: 'transform' } : {}), ...style }}
      onClick={!disabled ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive && !disabled ? 0 : undefined}
      onKeyDown={interactive && !disabled ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.();
      } : undefined}
    >
      {/* Spotlight glow — visible on hover via CSS */}
      {bento && !disabled && (
        <div
          className="bento-spotlight pointer-events-none absolute inset-0 z-0 opacity-0 rounded-lg transition-opacity duration-500"
          style={{
            background: 'radial-gradient(400px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(60,160,220,0.08), rgba(40,200,180,0.03) 40%, transparent 65%)',
          }}
        />
      )}
      <div className={bento ? 'relative z-10' : ''}>
        {children}
      </div>
    </div>
  );
}
